import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminPermissions, canAccessSectionSync } from '../utils/adminPermissions';

/**
 * Route Guard Component for Admin Routes
 * Protects admin routes based on user permissions
 */
const AdminRouteGuard = ({ children, requiredPermission, redirectTo = '/admin' }) => {
  const navigate = useNavigate();
  const { permissions, loading } = useAdminPermissions();
  const [hasAccess, setHasAccess] = useState(null);

  useEffect(() => {
    if (loading) return;

    const checkAccess = () => {
      if (!requiredPermission) {
        // No specific permission required, allow access
        setHasAccess(true);
        return;
      }

      const access = canAccessSectionSync(requiredPermission, permissions);
      setHasAccess(access);

      if (!access) {
        console.warn(`Access denied to ${requiredPermission}. Redirecting to ${redirectTo}`);
        navigate(redirectTo, { replace: true });
        
        // Show user-friendly message
        setTimeout(() => {
          alert('You do not have permission to access this section.');
        }, 100);
      }
    };

    checkAccess();
  }, [permissions, loading, requiredPermission, navigate, redirectTo]);

  // Show loading while checking permissions
  if (loading || hasAccess === null) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Checking permissions...</span>
      </div>
    );
  }

  // If access denied, don't render children (already redirected)
  if (!hasAccess) {
    return null;
  }

  // Render protected content
  return children;
};

export default AdminRouteGuard;